import { createAsyncAction, errorResult, successResult } from "pullstate";
import { GlobalStore } from "./GlobalStore";
import { Address } from "@wagmi/core";
import { BEBOP_GUILD_ID, GUILD_NAMES_TO_ID, GUILD_ROLES_PRECEDENCE } from "../../config/consts";
import { isLocal } from "../../config/env";
import { getIsUserConnected } from "utils/userUtils";
import * as Sentry from "@sentry/browser";

interface GuildSDKRole {
  id: number;
  name: string;
  description?: string;
  imageUrl?: string;
  createdAt: string;
  logic: "AND" | "OR" | "NAND" | "NOR";
  guildId: number;
  requirements: Array<never>;
  rolePlatforms?: Array<{
    guildPlatformId: number;
    platformRoleId?: string;
    platformRoleData?: Record<string, string>;
  }>;
  members: Array<string>;
}
type GetRoleResponse = GuildSDKRole & {
  id: number;
};
type GetUserAccessResponse = Array<{
  roleId: number;
  access: boolean;
}>;

export interface Role {
  id: number;
  access: boolean;
  precedence: number;
  name: string;
}

const GUILD_API_URL = "https://api.guild.xyz/v1";

export const fetchRoles = async (userAddress: Address): Promise<Array<Role>> => {
  if (userAddress) {
    const roles = await fetch(`${GUILD_API_URL}/guild/member/${BEBOP_GUILD_ID}/${userAddress}`, {
      headers: {
        ContentType: "application/json",
      },
    })
      .then(response => {
        return response
          .json()
          .then((rolesRaw: GetUserAccessResponse) => {
            rolesRaw = rolesRaw.filter(role =>
              Object.values(GUILD_NAMES_TO_ID).includes(role.roleId)
            );
            const roles: Promise<Array<Role>> = Promise.all(
              rolesRaw.map(async role => {
                const roleResponse = await getRole(role.roleId);
                return {
                  id: role.roleId,
                  name: roleResponse.name,
                  access: role.access,
                  precedence: GUILD_ROLES_PRECEDENCE[role.roleId],
                };
              })
            );
            return roles;
          })
          .catch(error => {
            console.log(`Could not parse guild roles`, error);
          });
      })
      .catch(error => {
        console.log(`Could not fetch guild roles`, error);
      });
    return roles ?? [];
  }
  return Promise.resolve([]);
};

export const getRoles = createAsyncAction(
  async ({ userAddress }: { userAddress: `0x${string}` }) => {
    try {
      const result = await fetchRoles(userAddress);
      if (result) {
        if (isLocal) {
          result.push({
            id: GUILD_NAMES_TO_ID.DEV_MOCK,
            name: "Beep boop Bebop",
            access: true,
            precedence: GUILD_ROLES_PRECEDENCE[GUILD_NAMES_TO_ID.DEV_MOCK],
          });
        }
        // Store in GlobalStore in precedence to prevent need for sorting every time a guild role is retrieved
        return successResult(result.sort((roleA, roleB) => roleA.precedence - roleB.precedence));
      }
    } catch (e) {
      console.log(`Could not get guild roles`, e);
      return errorResult([], `Could not get guild roles`, e);
    }
    return errorResult([], `Could not get guild roles`);
  },
  {
    postActionHook: ({ result }) => {
      if (!result.error && getIsUserConnected()) {
        GlobalStore.update(s => {
          s.userRoles = result.payload;
        });
      }
    },
  }
);

const getRole = async (roleId: number): Promise<GetRoleResponse> => {
  try {
    const response = await fetch(`${GUILD_API_URL}/role/${roleId}`);
    const json = await response.json();
    return json as GetRoleResponse;
  } catch (e) {
    console.log(`Could not get guild role`, e);
    Sentry.captureException(e);
  }
  return Promise.resolve({} as GetRoleResponse);
};
